import { useId, useState } from 'react'
import { RangeFields, SELECT_CLASS } from './ReportControls'
import { todayIso } from './reportDates'
import { exportWorkbook } from './reportExport'
import { loadWorkbookSheets } from './reportWorkbook'
import { useReportRange } from './useReportRange'

/**
 * Exports every report as one Excel workbook, a tab for each. The date range
 * applies to the range-based reports; the assignment sheet and the daily
 * roster take their own date and list every team.
 */
export default function WorkbookExport() {
  const range = useReportRange()
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [sheetDate, setSheetDate] = useState(todayIso())
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'done' | 'failed'; text: string } | null>(null)

  async function run() {
    setBusy(true)
    setNotice(null)
    const fileName = `muster-reports-${range.from}-to-${range.to}.xlsx`
    try {
      await exportWorkbook(await loadWorkbookSheets({ from: range.from, to: range.to, sheetDate, generatedAt: new Date() }), fileName)
      setNotice({ kind: 'done', text: `Exported ${fileName}` })
    } catch {
      setNotice({ kind: 'failed', text: 'The workbook could not be created. Try again.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="relative max-sm:contents print:hidden"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          setOpen(false)
        }
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
      >
        Export all reports to Excel
      </button>
      <section
        id={panelId}
        hidden={!open}
        aria-label="Export all reports"
        className="absolute top-full right-0 z-30 mt-2 w-[min(52rem,calc(100vw-2rem))] rounded-card border border-line bg-canvas p-4 text-left shadow-lg max-sm:static max-sm:w-full max-sm:basis-full"
      >
        <p className="mb-3 text-small text-ink-muted">
          One workbook with a tab for each report: daily roster, pay period schedule, daily coverage, two-week manpower, leave (requested and scheduled), detailed out, seniority roster,
          overtime planning, and the daily assignment sheet. The daily roster and the assignment sheet are for the date below; the pay period schedule is for the pay period the range ends in.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <RangeFields range={range} />
          <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
            Assignment sheet date
            <input type="date" value={sheetDate} onChange={(event) => setSheetDate(event.target.value)} className={SELECT_CLASS} />
          </label>
        </div>
        {range.invalid && (
          <p role="alert" className="mt-3 text-small text-error">
            The end date is before the start date. Choose an end date on or after the start.
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={busy || range.invalid || !sheetDate}
            onClick={run}
            className="min-h-11 rounded-control border border-primary bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-50"
          >
            {busy ? 'Preparing workbook' : 'Download workbook'}
          </button>
          <p role="status" className={`text-small ${notice?.kind === 'failed' ? 'text-error' : 'text-ink-muted'}`}>
            {notice?.text ?? ''}
          </p>
        </div>
      </section>
    </div>
  )
}
